import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import client from "@culturehq/client";
import { Dropdown, Icon, PlainButton, SearchBar } from "@culturehq/components";

import { sanitizeCategory } from "app/utils";
import tagsTypes from "constants/tagsTypes";
import useTags from "components/post/useTags";

import "../analytics/styles.scss";
import { debounce } from "lodash";
import fuzzyFilter from "app/utils/fuzzyFilter";
import getUserName from "app/utils/getUserName";
import BroadcastMessages from "./BroadcastMessages";

const TagOption = ({
  selected,
  label,
  category,
  onSelectedGroupsChange,
  option,
  icon
}) => (
  <PlainButton onClick={() => onSelectedGroupsChange(option)}>
    {selected && <><Icon icon="checkmark" className="checked-icon" /></>}
    {icon && <><Icon icon={icon} className="option-icon" /></>}
    {label}
    {category && <span className="option-category">({category})</span>}
  </PlainButton>
);

const TagFilter = ({ onChange }) => {
  const [addedOptions, setAddedOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [display, setDisplay] = useState("");
  const searchBarRef = useRef();
  const optionsCallback = undefined;
  const withOrgValues = false;
  const wholeCompany = false;
  const storyQuestions = [];
  const SEARCH_DELAY = 500;

  const { options } = useTags(
    optionsCallback, withOrgValues, wholeCompany, storyQuestions, addedOptions);

  const performSearch = async query => {
    const { entities } = await client.autocompleteMentions({ query });
    const users = entities.filter(entity => (
      entity.type === "users" && !addedOptions.some(option => option.value === entity.id)
    ));
    setAddedOptions([...addedOptions, ...users.map(user => (
      {
        category: tagsTypes.USERS,
        categoryIcon: "user",
        icon: "user-filled",
        label: getUserName(user),
        value: user.id
      }
    ))]);
  };

  const debouncedEnqueueSearch = useCallback(debounce(performSearch, SEARCH_DELAY), [addedOptions]);

  const handleTextChange = value => {
    setDisplay(value);
    debouncedEnqueueSearch(value);
  };

  const onSelectedGroupsChange = useCallback((option, currentTags = selectedTags) => {
    const deleteItem = existsIdx => {
      const newTags = [...currentTags];
      newTags.splice(existsIdx, 1);
      setSelectedTags(newTags);
    };

    const existsIdx = currentTags.findIndex(tag => tag.value === option.value);

    if (existsIdx !== -1) deleteItem(existsIdx);
    else setSelectedTags(prev => [...prev, { ...option, category: sanitizeCategory(option) }]);
  }, [selectedTags]);

  const onClearAllChange = () => { setSelectedTags([]); };

  useEffect(() => {
    const groupedCategories = () => selectedTags.reduce((acc, tag) => {
      if (!acc[tag.category]) {
        acc[tag.category] = [tag.value];
        return acc;
      }

      const existsIdx = acc[tag.category].findIndex(value => value === tag.value);
      if (existsIdx === -1) acc[tag.category].push(tag.value);
      return acc;
    }, {});

    onChange("tags", groupedCategories(), { selectedTags });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTags]);

  useEffect(() => {
    const bc1 = new BroadcastChannel(BroadcastMessages.ANALYTICS_TAG_FILTER_SELECT_TAGS);
    bc1.onmessage = (
      { data: { option, selectedTags } }) => onSelectedGroupsChange(option, selectedTags);

    const bc2 = new BroadcastChannel(BroadcastMessages.ANALYTICS_TAG_FILTER_CLEAR_ALL);
    bc2.onmessage = onClearAllChange;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredOptions = useMemo(() => fuzzyFilter(options, display, false), [display, options]);

  return (
    <Dropdown>
      <Dropdown.Button icon="android-options" className="chq-btn Gray-Button">
        Filters
        {!!selectedTags.length && `(${selectedTags.length})`}
      </Dropdown.Button>
      <Dropdown.ListBox className="Dp-List">
        <div className="TagFilter__Container">
          <SearchBar ref={searchBarRef} autoFocus throttle={0} placeholder="Search" onSearch={handleTextChange} />
          <div className="TagFilter__Options">
            {filteredOptions.map(option => {
              const { label, icon, category, value } = option;
              const selected = !!selectedTags.find(t => t.value === value);

              return (
                <TagOption
                  selected={selected}
                  label={label}
                  category={category}
                  onSelectedGroupsChange={onSelectedGroupsChange}
                  option={option}
                  icon={icon}
                />
              );
            })}
          </div>
        </div>
      </Dropdown.ListBox>
    </Dropdown>
  );
};

export default TagFilter;
